import { Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import cx from 'classnames'
import get from 'lodash/get'
import moment from 'moment'
import React from 'react'
import { Query } from 'react-apollo'
import Loader from '../../../common/loader/Loader'
import { RestrictedConsumer } from '../../auth/RestrictedContext'
import { i18n } from '../../index'
import RequiresAction from '../RequiresAction'
import css from '../weeklyInfoPage.css'
import { fetchWeeklyInfo } from '../weeklyInfoQueries'
import type { WeeklyInfoGeneral, WeeklyInfoVariables } from '../weeklyInfoTypes'

type HesburgerSalesAndHoursProps = {
  stationId: string
  date: string
}

export const HesburgerSalesAndHoursTable = ({ stationId, date }: HesburgerSalesAndHoursProps) => (
  <Query<Array<WeeklyInfoGeneral>, WeeklyInfoVariables>
    query={fetchWeeklyInfo}
    variables={{
      stationId,
      date,
      type: 'General',
    }}
  >
    {({ data, error, loading }) => (
      <Loader
        {...{
          error,
          loading,
        }}
      >
        <div className={css.salesAndHours}>
          <Typography variant='h6' gutterBottom>
            {i18n.t('weeklyInfo.title.salesAndHours', {
              week: `vko. ${moment(date).isoWeek()} ${moment(date).startOf('week').format('D.M.')}-${moment(date)
                .endOf('week')
                .format('D.M.')}`,
            })}
          </Typography>
          <RestrictedConsumer>
            {(hasPermission) => (
              <Paper>
                <Table className={css.table}>
                  <TableHead>
                    <TableRow>
                      <TableCell align='center' className={css.tableHeading} />
                      <TableCell align='center' className={cx(css.tableHeading, css.numericCell)}>
                        {i18n.t('weeklyInfo.heading.budgetedSales')}
                      </TableCell>
                      <TableCell align='center' className={cx(css.tableHeading, css.numericCell)}>
                        {i18n.t('weeklyInfo.heading.actualSales')}
                      </TableCell>
                      <TableCell align='center' className={cx(css.tableHeading, css.numericCell)}>
                        {i18n.t('weeklyInfo.heading.plannedSales')}
                      </TableCell>
                      <TableCell align='center' className={cx(css.tableHeading, css.numericCell)}>
                        {i18n.t('weeklyInfo.heading.actualHours')}
                      </TableCell>
                      <TableCell align='center' className={cx(css.tableHeading, css.numericCell)}>
                        {i18n.t('weeklyInfo.heading.plannedHours')}
                      </TableCell>
                      <TableCell align='center' className={cx(css.tableHeading, css.numericCell)}>
                        {i18n.t('weeklyInfo.heading.actualEfficiency')}
                      </TableCell>
                      <TableCell align='center' className={cx(css.tableHeading, css.numericCell)}>
                        {i18n.t('weeklyInfo.heading.plannedEfficiency')}
                      </TableCell>
                      <TableCell align='center' className={cx(css.tableHeading, css.numericCell)}>
                        {i18n.t('weeklyInfo.heading.budgetedEfficiency')}
                      </TableCell>
                      <TableCell align='center' className={cx(css.tableHeading, css.actionCell)}>
                        {hasPermission && i18n.t('weeklyInfo.heading.isActionRequired')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {get(data, 'weeklyInfo', []).map(
                      (item) =>
                        item.profitCenterType === 'Hesburger' && (
                          <HesburgerSalesAndHoursRow
                            stationId={stationId}
                            date={date}
                            data={item}
                            key={item.id}
                            canEdit={hasPermission}
                          />
                        ),
                    )}
                  </TableBody>
                </Table>
              </Paper>
            )}
          </RestrictedConsumer>
        </div>
      </Loader>
    )}
  </Query>
)

interface HesburgerSalesAndHoursRowProps {
  stationId: string
  date: string
  data: WeeklyInfoGeneral
  canEdit: boolean
}

const HesburgerSalesAndHoursRow = ({ stationId, date, data, canEdit }: HesburgerSalesAndHoursRowProps) => {
  const actualSales = Math.round(data.totalActualSales) || 0
  const plannedSales = Math.round(data.totalPlannedSales) || 0
  const budgetedSales = Math.round(data.totalBudgetedSales) || 0
  const actualHours = Math.round(data.totalActualHours) || 0
  const plannedHours = Math.round(data.totalPlannedHours) || 0
  const actualEfficiency = calculateEfficiency(actualSales, actualHours)
  const budgetedEfficiency = Math.round(data.budgetedEfficiency) || 0
  const plannedEfficiency = Math.round(data.plannedEfficiency) || 0
  return (
    <TableRow>
      <TableCell align='center' className={cx(css.tableCell, css.headingCell)}>
        {i18n.t(`weeklyInfo.heading.profitCenter${data.profitCenterType}`)}
      </TableCell>
      <TableCell align='center' className={cx(css.tableCell, css.numericCell)}>
        <strong>{budgetedSales} &euro;</strong>
      </TableCell>
      <TableCell
        align='center'
        className={cx(css.tableCell, css.numericCell, {
          [css.positiveCell]: actualSales >= plannedSales,
          [css.negativeCell]: actualSales < plannedSales,
        })}
      >
        {actualSales} &euro;
      </TableCell>
      <TableCell align='center' className={cx(css.tableCell, css.numericCell)}>
        {plannedSales} &euro;
      </TableCell>
      <TableCell
        align='center'
        className={cx(css.tableCell, css.numericCell, {
          [css.positiveCell]: actualHours <= plannedHours,
          [css.negativeCell]: actualHours > plannedHours,
        })}
      >
        {actualHours} h
      </TableCell>
      <TableCell align='center' className={cx(css.tableCell, css.numericCell)}>
        {plannedHours} h
      </TableCell>
      <TableCell
        align='center'
        className={cx(css.tableCell, css.numericCell, {
          [css.positiveCell]: actualEfficiency >= plannedEfficiency,
          [css.negativeCell]: actualEfficiency < plannedEfficiency,
        })}
      >
        {actualEfficiency}
      </TableCell>
      <TableCell align='center' className={cx(css.tableCell, css.numericCell)}>
        {plannedEfficiency}
      </TableCell>
      <TableCell align='center' className={cx(css.tableCell, css.numericCell)}>
        <strong>{budgetedEfficiency}</strong>
      </TableCell>
      <TableCell align='center' className={cx(css.tableCell, css.actionCell)}>
        {canEdit && (
          <RequiresAction
            stationId={stationId}
            date={date}
            weeklyInfoId={data.id}
            subject='Management'
            isActionRequired={data.isActionRequired}
          />
        )}
      </TableCell>
    </TableRow>
  )
}

const calculateEfficiency = (actualSales: number, actualHours: number): number =>
  actualSales && actualHours ? Math.round(actualSales / actualHours) : 0
